/*
 * Which attention items deserve a banner. Items already there on first load
 * never banner (they're in the Agents menu); a new item, or a new signal on
 * the same thread, banners once; a snoozed item banners again when its snooze
 * lapses and it is still unresolved.
 */
import { isSnoozed } from "./items";
import type { AttentionItem } from "./types";

export const announceKey = (
  item: Pick<AttentionItem, "thread_id" | "since">
): string => `${item.thread_id}:${item.since}`;

export type Announcer = {
  /** The items to banner now, given the provider's current list. */
  evaluate: (items: AttentionItem[], now?: number) => AttentionItem[];
  /** When the next snooze lapses (ms epoch), to re-evaluate then. */
  nextLapse: (items: AttentionItem[], now?: number) => number | null;
};

export function createAnnouncer(): Announcer {
  const seen = new Set<string>();
  const snoozed = new Set<string>();
  let primed = false;

  return {
    evaluate(items, now = Date.now()) {
      const out = items.filter((item) => {
        const key = announceKey(item);
        if (isSnoozed(item, now)) {
          seen.add(key);
          snoozed.add(key);
          return false;
        }
        if (snoozed.delete(key)) return true;
        if (seen.has(key)) return false;
        seen.add(key);
        return primed;
      });
      primed = true;
      return out;
    },
    nextLapse(items, now = Date.now()) {
      let next: number | null = null;
      for (const item of items) {
        const t = item.snoozed_until ? Date.parse(item.snoozed_until) : NaN;
        if (Number.isFinite(t) && t > now && (next === null || t < next))
          next = t;
      }
      return next;
    }
  };
}

/** How long to wait before re-checking a snooze; capped so far-off ends don't overflow a timer. */
export const lapseDelay = (next: number, now: number): number =>
  Math.min(Math.max(0, next - now) + 50, 60 * 60 * 1000);
